<template>
  <div class="border w-100 pa-2" :style="{ flex: 1, position: 'relative' }">
    <v-overlay :value="isLoading" absolute color="white" opacity="0.7">
      <v-progress-circular indeterminate color="#E7000B" />
    </v-overlay>
    <!--TITLE, SUBTITLE -->
    <div class="d-flex flex-column align-center justify-center gap-1">
      <p class="ma-0 text-center text-subtitle-1 font-weight-medium">
        {{ isEventGuest ? 'Evento por Invitación' : 'Confirmar Asistencia' }}
      </p>

      <p class="ma-0 text-center font-weight-light">
        {{
          isEventGuest
            ? 'Anteriormente, confirmaste tu asistencia a este evento como invitado'
            : 'Esto registrará tu asistencia y habilitará la documentación'
        }}
      </p>
    </div>

    <v-divider />
    <!-- CONTENT -->
    <div
      class="d-flex flex-column justify-center align-center pa-5"
      :style="{ gap: '24px' }"
    >
      <div
        class="w-100 rounded-lg pa-3"
        :style="{ flex: 1, backgroundColor: '#F9FAFB' }"
      >
        <p
          v-for="{ id, label, value } in eventDetails"
          :key="id"
          class="ma-0 text-left font-weight-bold"
        >
          {{ label }}: <span class="font-weight-regular">{{ value }}</span>
        </p>
      </div>

      <!-- ALERT -->
      <div v-if="!isEventGuest" class="w-100">
        <div>
          <p class="mb-2" :style="{ color: '#E7000B' }">
            <v-icon class="mr-1" color="#E7000B" small>
              mdi-alert-circle-outline
            </v-icon>
            Importante
          </p>
          <ul>
            <li>Una vez confirmado el evento, no se puede cancelar</li>
            <li>Se registrará tu ubicación actual al confirmar el evento</li>
          </ul>
        </div>

        <v-text-field
          v-model="safeWord"
          class="my-4"
          :disabled="isLoading || isReadOnly"
          filled
          :hint="`Escribe ${EVENT_CONFIRMATION_SAFE_WORD} para continuar`"
          :loading="isLoading"
          persistent-hint
        />

        <div class="d-flex flex-column align-center justify-end gap-2">
          <div class="d-flex align-center justify-center gap-2">
            <v-btn
              color="#E20809"
              :disabled="!isValidSafeWord || !canUpdateEvent || isReadOnly"
              :elevation="0"
              small
              :style="{ color: 'white' }"
              @click="onSubmit(EventConfirmationStatus.NOT_CONFIRMED)"
            >
              No Realizado
            </v-btn>
            <v-btn
              color="#1BA13C"
              :disabled="!isValidSafeWord || !canUpdateEvent || isReadOnly"
              :elevation="0"
              small
              :style="{ color: 'white' }"
              @click="onSubmit(EventConfirmationStatus.CONFIRMED)"
            >
              Realizado
            </v-btn>
          </div>
          <v-btn
            color="#AF64FD"
            :disabled="!canUpdateEvent || isReadOnly"
            :elevation="0"
            small
            text
            @click="onReschedule"
          >
            Reprogramar
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { EVENT_CONFIRMATION_SAFE_WORD } from '@/core/constants/schedule/notification';
  import {
    EventAction,
    EventConfirmationStatus,
    EventType,
  } from '@/core/constants/schedule/event';
  import useGeolocation from '@/mixins/useGeolocation';
  import { matchEvent } from '@/utilities/schedule/event';
  import { eventBus } from '@/main';

  import { mapGetters, mapActions } from 'vuex';

  export default {
    name: 'ScheduleEventConfirmationStep',
    mixins: [useGeolocation],
    props: {
      targetEvent: {
        type: Object,
        required: true,
      },
    },
    data: () => {
      return {
        EventConfirmationStatus,
        isLoading: false,
        safeWord: null,
        userGeolocation: null,
        EVENT_CONFIRMATION_SAFE_WORD,
      };
    },
    computed: {
      ...mapGetters('auth', ['user']),
      ...mapGetters('schedule', ['events', 'canUpdateEvent']),
      ...mapGetters('scheduleNotification', ['todayEvents']),
      event() {
        if (!this.targetEvent) return null;
        return (
          this.events.find((e) => matchEvent(e, this.targetEvent)) ??
          this.todayEvents.find((e) => matchEvent(e, this.targetEvent)) ??
          null
        );
      },
      isReadOnly() {
        if (!this.event) return true;
        return this.user?.id !== this.event.creator?.user?.id;
      },
      isEventGuest() {
        return this.event?.participants?.some(
          (p) => p.user.id === this.user?.id && p.type.value === 'guest'
        );
      },
      isValidSafeWord() {
        return this.safeWord?.toUpperCase() === EVENT_CONFIRMATION_SAFE_WORD;
      },
      eventDetails() {
        const event = this.event;

        return [
          { id: 'type', label: 'Tipo', value: event?.type.label },
          {
            id: 'activity',
            label: 'Actividad',
            value: event?.activity?.activity?.label,
          },
          {
            id: 'customer',
            label: 'Cliente',
            value: event?.customer
              ? `${event.customer.code} ${event.customer.name}`
              : null,
          },
          {
            id: 'prospect',
            label: 'Prospecto',
            value:
              event?.type?.value === EventType.PROSPECTION
                ? event.prospect?.businessName
                : null,
          },
          {
            id: 'location',
            label: 'Ubicación',
            value: event?.location,
          },
          {
            id: 'comment',
            label: 'Notas',
            value: event?.comment,
          },
        ].filter((d) => d.value);
      },
    },
    created() {
      this.$store.dispatch('geolocation/checkPermission');
    },
    methods: {
      ...mapActions('schedule', ['createEventExecution']),
      async onSubmit(confirmationStatus) {
        if (this.isReadOnly) return;
        this.isLoading = true;

        const geolocation = await this.getGeolocationPosition();

        const { data, error } = await this.createEventExecution({
          eventId: this.event.id,
          execution: {
            confirmationStatus,
            occurrenceId: this.event.occurrenceId,
          },
          action: {
            userId: this.user?.id,
            type: EventAction.CONFIRM_EVENT,
            geolocation,
          },
        });

        if (error) {
          this.$toast.error(error);
          this.isLoading = false;
          return;
        }

        if (confirmationStatus === EventConfirmationStatus.CONFIRMED) {
          if (!data.message) {
            this.$toast.success('Evento confirmado con éxito');
            eventBus.$emit('scheduleOpenModalEvent', {
              event: data.event,
              tab:
                data.event.type.value === EventType.CUSTOMER_VISIT
                  ? 'history'
                  : 'details',
            });
          } else {
            this.$toast.warning(data.message);
          }
        } else {
          this.$toast.warning('Evento confirmado como no realizado');
        }

        this.isLoading = false;

        eventBus.$emit('scheduleEventsHistoryRefresh');
        this.$emit('onSubmit');
      },
      onReschedule() {
        if (this.isReadOnly) return;
        eventBus.$emit('scheduleOpenModalEvent', { event: this.event });
        this.$emit('onReschedule');
      },
    },
  };
</script>
